import { useShallow } from 'zustand/react/shallow'
import { useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { partialContentUpdate } from 'api/content'
import { CompleteIcon } from 'assets/icons'
import CreateCardPlaceholder from 'components/CreateCardPlaceholder'
import Sidebar from 'components/Sidebar'
import SidebarTabs from 'components/Sidebar/SidebarTabs'
import useRelationsContent from 'hooks/api/useRelationsContent'
import { capitalize, getImageProcessorUrl, t } from 'helpers'
import { usePortfolioStore, useUserStore } from 'store'
import { Content } from 'types/app'
import { PORTFOLIO_TYPES, RELATIONS, UPDATE_STRATEGY } from 'types/enums'
import {
	attributeBasicFields,
	attributeSpecialFields,
	picturesFields,
	showreelsFields,
} from 'pages/Portfolio/fields'
import s from './index.module.scss'

const maxItems = 16

const GallerySidebar = (): JSX.Element => {
	const swiperRef = useRef<SwiperClass>()
	const [activeTab, setActiveTab] = useState(0)
	const [galleryType, setGalleryType] = usePortfolioStore(
		useShallow((state) => [state.galleryType, state.setGalleryType]),
	)
	const userId = useUserStore(useShallow((state) => state.userId))

	const { data } = useRelationsContent(
		galleryType ? [RELATIONS.IMAGE, RELATIONS.BEST_PERFORMANCES] : null,
		userId,
		undefined,
		'',
		false,
		true,
	)

	const images = data?.[RELATIONS.IMAGE] || []

	const videos = data?.[RELATIONS.BEST_PERFORMANCES] || []

	const createMediaList = (items: Content[]): Content[] =>
		Array.from({ length: maxItems })?.map(
			(_, index) =>
				items?.find((item) => item.relationIndex === index) || undefined,
		) as Content[]

	const selectMedia = async (id: string, strategy: number): Promise<void> => {
		const { setGalleryValues, generalModalType } = usePortfolioStore.getState()
		setGalleryValues(
			id,
			`${galleryType?.mainSelectorName}:${galleryType?.value}`,
		)

		await partialContentUpdate(
			id,
			{
				labels: {
					[capitalize(generalModalType)]: [
						`${galleryType?.mainSelectorName}:${galleryType?.value}`,
					],
				},
			},
			`?strategy=${strategy}`,
		)
	}

	const close = (): void => {
		setGalleryType(null)
		setActiveTab(0)
	}

	return (
		<Sidebar
			open={!!galleryType}
			onClose={close}
			iconClose
			header={{
				title: t('APP_ASSIGN_MEDIA_TITLE'),
			}}
			additional>
			<>
				<SidebarTabs
					active={activeTab}
					onChange={(v) => {
						setActiveTab(v as number)
						swiperRef.current?.slideTo(v as number)
					}}
					options={[
						{ label: 'APP_PROFILE_MEDIA', value: 0 },
						{
							label: 'APP_PROFILE_SHOWREELS',
							value: 1,
						},
					]}
				/>
				<Swiper
					onSwiper={(swiper: SwiperClass) => {
						swiperRef.current = swiper
					}}
					allowTouchMove={false}
					style={{ width: '100%' }}>
					<SwiperSlide>
						<div className={s.list}>
							{createMediaList(images)?.map((data, index) => (
								<GalleryItem
									data={data}
									onClick={selectMedia}
									key={data ? data?.ContentId : index}
									activeTab={activeTab}
									index={index}
								/>
							))}
						</div>
					</SwiperSlide>
					<SwiperSlide>
						<div className={s.list}>
							{createMediaList(videos)?.map((data, index) => (
								<GalleryItem
									data={data}
									onClick={selectMedia}
									key={data ? data?.ContentId : index}
									activeTab={activeTab}
									index={index}
								/>
							))}
						</div>
					</SwiperSlide>
				</Swiper>
			</>
		</Sidebar>
	)
}

export default GallerySidebar

const GalleryItem = ({
	data,
	onClick,
	activeTab,
	index,
}: {
	data: Content
	onClick: (id: string, strategy: number) => void
	activeTab: number
	index: number
}): JSX.Element => {
	const [galleryType, generalType, modalType, cardValues, galleryValues] =
		usePortfolioStore(
			useShallow((state) => [
				state.galleryType,
				state.generalModalType,
				state.modalType,
				state.cardValues,
				state.galleryValues,
			]),
		)

	const localName =
		(activeTab === 0 ? PORTFOLIO_TYPES.PICTURE : PORTFOLIO_TYPES.SHOWREEL) +
		index

	const localValue = cardValues?.[localName]

	const selected = useMemo(
		() =>
			Object.entries(galleryValues)
				.filter((values) =>
					values?.[1]?.includes(
						`${galleryType?.mainSelectorName}:${galleryType?.value}`,
					),
				)
				.map(([contentId]) => contentId)
				?.includes(data?.ContentId) || localValue?.selected,
		[
			data?.ContentId,
			galleryType?.mainSelectorName,
			galleryType?.value,
			galleryValues,
			localValue?.selected,
		],
	)

	const disableMedia =
		[
			attributeBasicFields.body.value,
			attributeSpecialFields.bodyDone.value,
			attributeSpecialFields.bodyWilling.value,
		].includes(modalType) && data?.ContentLabelType === 'FULLSHAPE'

	return (
		<div
			className={cn({
				[s.active]: selected,
				[s.disabled]: data && disableMedia,
			})}
			onClick={() => {
				let strategy = UPDATE_STRATEGY.ADD
				if (selected) {
					strategy = UPDATE_STRATEGY.REMOVE
				}

				if (data?.ContentId) onClick(data?.ContentId, strategy)
				else if (localValue?.contentId) {
					onClick(localValue.contentId as string, strategy)
					const { setCardValues } = usePortfolioStore.getState()
					setCardValues(
						{
							selected: true,
						},
						localName,
					)
				} else {
					const {
						setModalType,
						setGeneralModalType,
						setuploadImage,
						setGalleryType,
						cardData,
						uploadImageData,
						setPrevSidebarData,
					} = usePortfolioStore.getState()
					setPrevSidebarData({
						generalType,
						cardData,
						uploadImageData,
						modalType,
						galleryType,
					})
					setGalleryType(null)
					setGeneralModalType(
						activeTab === 0
							? PORTFOLIO_TYPES.PICTURE
							: PORTFOLIO_TYPES.SHOWREEL,
					)
					setModalType(localName)
					setuploadImage(
						activeTab === 0 ? picturesFields[index] : showreelsFields[index],
					)
				}
			}}>
			{selected ? (
				<div
					style={{
						display: 'flex',
						position: 'absolute',
						top: '5px',
						left: '5px',
					}}>
					<CompleteIcon />
				</div>
			) : null}
			{localValue?.[localName] instanceof File ? (
				<img
					src={
						URL.createObjectURL(localValue[localName] as File) ||
						'/placeholder.png'
					}
					onError={(e) => {
						const target = e.target as HTMLImageElement
						target.src = '/placeholder.png'
					}}
					alt="gallery-assign"
				/>
			) : data ? (
				<>
					<img
						alt="gallery-assign"
						src={
							getImageProcessorUrl(data?.ContentSquareImageUrl, 388) ||
							'/placeholder.png'
						}
						onError={(e) => {
							const target = e.target as HTMLImageElement
							target.src = '/placeholder.png'
						}}
					/>
					<span className="body2-m">{data?.ContentTitle}</span>
				</>
			) : (
				<CreateCardPlaceholder
					label={activeTab === 0 ? 'APP_ADD_PICTURE' : 'APP_ADD_REEL'}
					onClick={() => null}
				/>
			)}
		</div>
	)
}
